import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { ComponentCallDirection, ComponentChannel } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateWorkflow } from '@/hooks/use-workflows';
import { channelNotice } from './graph';

/**
 * A new workflow starts as a greeting and a goodbye with no words yet.
 * Which way the call goes and on what channel are fixed here, because they
 * decide which steps it may use.
 */
export function NewWorkflowDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}): React.ReactElement {
  const create = useCreateWorkflow();
  const [name, setName] = useState('');
  const [direction, setDirection] = useState<ComponentCallDirection>('outbound');
  const [channel, setChannel] = useState<ComponentChannel>('voice');
  const notice = channelNotice(channel);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New workflow</DialogTitle>
          <DialogDescription>It starts with a greeting and a goodbye.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <label htmlFor="workflow-name" className="text-sm font-medium">
              Name
            </label>
            <Input
              id="workflow-name"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium">Who starts it</div>
            <Select
              value={direction}
              onValueChange={(v) => setDirection(v as ComponentCallDirection)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="outbound">We call the patient</SelectItem>
                <SelectItem value="inbound">The patient calls us</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium">Channel</div>
            <Select value={channel} onValueChange={(v) => setChannel(v as ComponentChannel)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="voice">Phone call</SelectItem>
                <SelectItem value="sms">Text messages</SelectItem>
                <SelectItem value="chat">Web chat</SelectItem>
              </SelectContent>
            </Select>
            {notice && <p className="text-xs text-muted-foreground">{notice}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || create.isPending}
            onClick={() =>
              create.mutate(
                { name: name.trim(), direction, channel },
                {
                  onSuccess: (detail) => {
                    setName('');
                    onOpenChange(false);
                    onCreated(detail.id);
                  },
                }
              )
            }
          >
            {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
